import React, { useState, useEffect } from 'react';
import { Clock, Play, Square, LogOut } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

export default function InternDashboard() {
  const [tasks, setTasks] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const COLUMNS = ['todo', 'in_progress', 'review', 'done'];

  useEffect(() => {
    const userData = localStorage.getItem('internUser');
    if (!userData) {
      navigate({ to: '/intern/login' });
      return;
    }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    fetchTasks(parsedUser.id);
  }, [navigate]);

  const fetchTasks = async (internId) => {
    try {
      const token = localStorage.getItem('internToken');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/interns/${internId}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setTasks(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const handleDrop = async (e, newStatus) => {
    const taskId = e.dataTransfer.getData('taskId');
    if (!taskId) return;
    
    setTasks(prev => prev.map(t => t.id === Number(taskId) ? { ...t, status: newStatus } : t));
    
    try {
      const token = localStorage.getItem('internToken');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
    } catch (err) {
      fetchTasks(user.id);
    }
  };

  const toggleTimer = async (taskId, action) => {
    try {
      const token = localStorage.getItem('internToken');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}/time`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action })
      });
      if (res.ok) {
        setActiveTask(action === 'start' ? taskId : null);
        if (action === 'stop') fetchTasks(user.id); // refresh times
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black p-8 text-white">
      <div className="max-w-7xl mx-auto space-y-8">
        <header className="flex justify-between items-center bg-[#111] p-6 rounded-2xl border border-white/10">
          <div>
            <h1 className="text-3xl font-bold">Intern Dashboard</h1>
            <p className="text-gray-400 mt-1">{user.email}</p>
          </div>
          <button 
            onClick={() => { localStorage.removeItem('internToken'); localStorage.removeItem('internUser'); navigate({ to: '/intern/login' }); }}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" /> Logout
          </button>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {COLUMNS.map(col => (
            <div 
              key={col}
              onDragOver={e => e.preventDefault()}
              onDrop={e => handleDrop(e, col)}
              className="bg-[#111] border border-white/10 rounded-xl p-4 min-h-[600px]"
            >
              <h3 className="text-silver font-bold uppercase tracking-wider mb-4 border-b border-white/10 pb-2">
                {col.replace('_', ' ')}
              </h3>
              
              <div className="space-y-4">
                {tasks.filter(t => t.status === col).map(task => (
                  <div 
                    key={task.id}
                    draggable
                    onDragStart={e => e.dataTransfer.setData('taskId', task.id)}
                    className="bg-black border border-white/20 p-4 rounded-lg cursor-grab active:cursor-grabbing hover:border-silver transition-colors"
                  >
                    <div className="font-medium mb-3">{task.title}</div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center text-gray-400">
                        <Clock className="w-4 h-4 mr-1" /> {task.timeLoggedMinutes || 0}m
                      </span>
                      
                      {col === 'in_progress' && (
                        <button 
                          onClick={() => toggleTimer(task.id, activeTask === task.id ? 'stop' : 'start')}
                          className={`p-2 rounded-full transition-colors ${activeTask === task.id ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'}`}
                        >
                          {activeTask === task.id ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
